import InfiniteScroll from "@/modern/components/infinite-scroll";
import { cn } from "@/modern/utils";
import type { PromoteSurfaceAsset, SurfaceTab } from "../api/types";
import PromoteSurfaceCard from "./PromoteSurfaceCard";
import PromoteSurfaceReviewBatchFooter from "./PromoteSurfaceReviewBatchFooter";

type Props = {
  assets: PromoteSurfaceAsset[];
  selectedIds: number[];
  focusedAssetId: number | null;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  onSelect: (assetId: number, opts?: { range?: boolean }) => void;
  onOpen: (assetId: number) => void;
  readOnly?: boolean;
  tab?: SurfaceTab;
};

export default function PromoteSurfaceGrid({
  assets,
  selectedIds,
  focusedAssetId,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  onSelect,
  onOpen,
  readOnly,
  tab,
}: Props) {
  if (isLoading && assets.length === 0) {
    return (
      <div className="grid h-full grid-cols-2 gap-2 overflow-y-auto px-4 pb-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {Array.from({ length: 18 }).map((_, i) => (
          <div key={i} className="aspect-square animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  }

  const showReviewBatchFooter =
    tab === "review" && assets.length > 0 && !hasNextPage && !isFetchingNextPage;

  return (
    <div className="h-full overflow-y-auto px-4 pb-4 pt-1">
      <InfiniteScroll
        hasMore={hasNextPage}
        isLoading={isFetchingNextPage}
        next={fetchNextPage}
        threshold={0.5}
      >
        <div className="grid grid-cols-2 gap-2.5 p-0.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {assets.map((asset) => (
            <PromoteSurfaceCard
              key={asset.id}
              asset={asset}
              selected={selectedIds.includes(asset.id)}
              focused={focusedAssetId === asset.id}
              readOnly={readOnly}
              onSelect={(event) => {
                onSelect(asset.id, { range: event.shiftKey });
              }}
              onOpen={() => onOpen(asset.id)}
            />
          ))}
        </div>
        {(isFetchingNextPage || hasNextPage) && (
          <div
            className={cn(
              "flex items-center justify-center py-4 text-xs text-muted-foreground",
              isFetchingNextPage && "animate-pulse"
            )}
          >
            {isFetchingNextPage ? "Loading more…" : "Scroll for more"}
          </div>
        )}
        {showReviewBatchFooter && <PromoteSurfaceReviewBatchFooter />}
      </InfiniteScroll>
    </div>
  );
}
